/**
 * ConfidenceCheckin — occasional Dashboard prompt asking the user to rate
 * their overall confidence. Auto-hides via /lms/confidence/should-prompt.
 *
 * When the user rates ≤ 2 or asks for help, we route them to /learning with
 * `?ai_help=...` so the Prof. FreedrIQ opens pre-loaded with their
 * struggle context.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import ConfidenceSurvey from './ConfidenceSurvey';
import { Button } from './ui/button';
import { Sparkles, X } from 'lucide-react';

const DISMISS_KEY = 'confidence_checkin_dismissed_at_v1';

const ConfidenceCheckin = () => {
  const { API } = useContext(AuthContext);
  const [shouldPrompt, setShouldPrompt] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Client-side snooze — 24 hours after Dismiss so we don't nag the same day.
    try {
      const raw = window.localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const at = Number(raw);
        if (Number.isFinite(at) && Date.now() - at < 24 * 60 * 60 * 1000) {
          setDismissed(true);
          return;
        }
      }
    } catch (_) { /* noop */ }

    let cancelled = false;
    axios.get(`${API}/lms/confidence/should-prompt`)
      .then((res) => { if (!cancelled) setShouldPrompt(!!res.data?.prompt); })
      .catch(() => { /* silent — feature is optional */ });
    return () => { cancelled = true; };
  }, [API]);

  const dismiss = () => {
    try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* noop */ }
    setDismissed(true);
  };

  if (dismissed || !shouldPrompt) return null;

  return (
    <>
      <div
        className="pro-card p-4 bg-primary/5 border-primary/30 relative"
        data-testid="confidence-checkin-card"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-2 right-2 h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
          aria-label="Dismiss check-in"
          data-testid="confidence-checkin-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
              How are you doing?
            </div>
            <div className="font-heading text-sm font-extrabold text-foreground mt-0.5">
              Quick check-in on your learning
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              A one-tap rating keeps your path honest. If you{'\u2019'}re stuck, we{'\u2019'}ll route you to the AI Teacher.
            </p>
            <Button
              size="sm"
              onClick={() => setSurveyOpen(true)}
              data-testid="confidence-checkin-open"
              className="mt-3 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Rate my confidence
            </Button>
          </div>
        </div>
      </div>

      <ConfidenceSurvey
        open={surveyOpen}
        onClose={() => {
          setSurveyOpen(false);
          setShouldPrompt(false);   // hide the card until the next 7-day window
        }}
        trigger="dashboard_checkin"
        context={{}}
        API={API}
      />
    </>
  );
};

export default ConfidenceCheckin;
